import React, { useState, useCallback, useRef, SetStateAction } from 'react';
import { api } from '@services'; // Assuming @services alias is configured
import {
  FinancialDataAPI,
  FinancialGridRow,
  EditedRows
} from '@features/ocr/types/types'; // Assuming types path

// Import EditRecord separately to avoid conflict with local declaration
import type { EditRecord } from '@features/ocr/types/types';


// Define EnhancedFinancialGridRow locally (same as in ProfitLossStatement.tsx)
interface EnhancedFinancialGridRow extends FinancialGridRow {
  categoryName: string;
  [year: string]: string | number | boolean | undefined | null;
}

// Define error type for API responses
interface ApiError {
  response?: {
    data?: {
      details?: string;
      message?: string;
    };
    status?: number;
  };
  message?: string;
}

// We'll use the imported EditRecord type directly

interface UseEditableDataReturn {
  gridRows: FinancialGridRow[];
  editedRows: EditedRows;
  error: string | null;
  loading: boolean;
  handleRowUpdate: (updatedRow: FinancialGridRow) => Promise<FinancialGridRow>;
  handleRowUpdateError: (error: Error) => void;
  resetEditedRows: () => void;
  saveChanges: () => Promise<void>;
  addFormCategory: number | '';
  addFormItemName: string;
  addFormYear: string;
  addFormAmount: string;
  setAddFormCategory: React.Dispatch<SetStateAction<number | ''>>;
  setAddFormItemName: React.Dispatch<SetStateAction<string>>;
  setAddFormYear: React.Dispatch<SetStateAction<string>>;
  setAddFormAmount: React.Dispatch<SetStateAction<string>>;
  deletedRowIds: Set<string>;
  setDeletedRowIds: React.Dispatch<SetStateAction<Set<string>>>;
  rowToDelete: FinancialGridRow | null;
  deleteConfirmOpen: boolean;
  saving: boolean;
  setSaving: React.Dispatch<SetStateAction<boolean>>;
  setGridRows: React.Dispatch<SetStateAction<FinancialGridRow[]>>; // Added this line
  setEditedRows: React.Dispatch<SetStateAction<EditedRows>>; // Added this line
  setRowToDelete: React.Dispatch<SetStateAction<FinancialGridRow | null>>;
  setDeleteConfirmOpen: React.Dispatch<SetStateAction<boolean>>;
  handleDeleteClick: (row: FinancialGridRow) => void;
  handleDeleteConfirm: () => void;
  handleDeleteCancel: () => void;
  processRowUpdate: (
    newRow: EnhancedFinancialGridRow,
    oldRow: EnhancedFinancialGridRow,
    params: { rowId: string }
  ) => EnhancedFinancialGridRow | Promise<EnhancedFinancialGridRow>;

  // Wrapper for 2-arg signature
  processRowUpdate2Arg: (
    newRow: FinancialGridRow,
    oldRow: FinancialGridRow
  ) => Promise<FinancialGridRow>;
  onProcessRowUpdateError: (error: Error) => void;
  handleSaveAllChanges: (
    selectedHotelId: string, // Changed name for clarity
    financialData: FinancialDataAPI | null,
    selectedDocType: string,
    setSuccess: (message: string | null) => void,
    setError: (message: string | null) => void,
    fetchFinancialData: (hotelId: string) => Promise<void>
  ) => Promise<void>;
  handleAddNewItem: (
    selectedHotelId: string, // Changed name for clarity
    financialData: FinancialDataAPI | null,
    selectedDocType: string,
    setSuccess: (message: string | null) => void,
    setError: (message: string | null) => void,
    fetchFinancialData: (hotelId: string) => Promise<void>,
    // Make metric fetches optional if they aren't always needed/available
    fetchADRMetricsData?: (hotelId: string) => Promise<void>,
    fetchOccupancyMetricsData?: (hotelId: string) => Promise<void>,
    fetchRevPARMetricsData?: (hotelId: string) => Promise<void>
  ) => Promise<void>;
  updateGridRowsFromFinancialData: (
    financialData: FinancialDataAPI | null
  ) => void;
  hasChanges: boolean;
  setHasChanges: React.Dispatch<SetStateAction<boolean>>;
}

// Utility function for data transformation (can be outside the hook)
const transformFinancialDataToGridRows = (
    financialDataInput: FinancialDataAPI | null,
    internalIdCounter: React.MutableRefObject<number> // Pass ref
): FinancialGridRow[] => {
    if (!financialDataInput?.data) {
        console.warn('No data in financialDataInput:', financialDataInput);
        return [];
    }

    const rows: FinancialGridRow[] = [];
    const years = financialDataInput.years || []; // Ensure years is an array

    console.log('Processing financial data with document types:', Object.keys(financialDataInput.data));
    console.log('Years from financial data:', years);

    Object.entries(financialDataInput.data).forEach(([docType, docTypeData]) => {
        if (!docTypeData) {
            console.warn(`Skipping empty docTypeData for ${docType}`);
            return; // Skip if docType data is missing
        }

        console.log(`Processing docType: ${docType} with categories:`, Object.keys(docTypeData));

        Object.entries(docTypeData).forEach(([catIdStr, items]) => {
            const categoryId = Number(catIdStr);
            if (isNaN(categoryId)) {
                console.warn(`Invalid category ID: ${catIdStr} in docType: ${docType}`);
                return;
            }

            if (!Array.isArray(items)) {
                console.warn(`Items not an array for category ID: ${catIdStr} in docType: ${docType}`);
                return;
            }

            console.log(`Processing category ID: ${categoryId} with ${items.length} items`);

            items.forEach((item) => {
                if (!item) {
                    console.warn(`Null item in category ID ${categoryId}, docType ${docType}`);
                    return;
                }

                if (typeof item.item_name !== 'string') {
                    console.warn(`Item name is not a string in category ID ${categoryId}, docType ${docType}:`, item);
                    return;
                }

                // Handle different value formats (string JSON or object)
                let itemValues: Record<string, number | null> = {};

                if (typeof item.values === 'string') {
                    try {
                        itemValues = JSON.parse(item.values);
                        console.log(`Parsed values string for ${item.item_name}:`, itemValues);
                    } catch (e) {
                        console.error(`Failed to parse values string for ${item.item_name}:`, e);
                        return;
                    }
                } else if (typeof item.values === 'object' && item.values !== null) {
                    itemValues = item.values;
                    console.log(`Using values object for ${item.item_name}:`, itemValues);
                } else {
                    console.warn(`Invalid values format for ${item.item_name}:`, item.values);
                    return;
                }

                const rowId = `${docType}-${categoryId}-${item.item_name}`; // Ensure unique ID format

                // Create a base row without year values
                const baseRow = {
                    id: rowId,
                    internalId: internalIdCounter.current++, // Use passed ref
                    originalCategoryId: categoryId,
                    originalItemName: item.item_name, // Store original item name
                    categoryId: categoryId,
                    itemName: item.item_name,
                    documentType: docType,
                    fileId: item.file_id,
                };

                // Add year values with proper null handling
                const yearValues = {} as Record<string, number>;
                years.forEach(year => {
                    const originalValue = itemValues[year];
                    console.log(`Processing year ${year} for ${item.item_name}: originalValue=${originalValue}, typeof=${typeof originalValue}`);

                    // Convert to number, defaulting to 0 if null/undefined/invalid
                    const numValue = originalValue !== undefined && originalValue !== null
                        ? (typeof originalValue === 'number' ? originalValue : parseFloat(String(originalValue)))
                        : 0;
                    yearValues[year] = isNaN(numValue) ? 0 : numValue;
                });

                // Combine base row with year values
                const row = {
                    ...baseRow,
                    ...yearValues
                } as FinancialGridRow;

                console.log(`Created row for ${item.item_name}:`, Object.keys(row));

                rows.push(row);
            });
        });
    });

    console.log(`Transformed ${rows.length} rows from financial data`);
    return rows;
};


export function useEditableData(): UseEditableDataReturn {
  // Use standard useState setters
  const [gridRows, setGridRows] = useState<FinancialGridRow[]>([]);
  const [editedRows, setEditedRows] = useState<EditedRows>({});
  const [deletedRowIds, setDeletedRowIds] = useState<Set<string>>(new Set());

  const [addFormCategory, setAddFormCategory] = useState<number | ''>('');
  const [addFormItemName, setAddFormItemName] = useState<string>('');
  const [addFormYear, setAddFormYear] = useState<string>('');
  const [addFormAmount, setAddFormAmount] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [rowToDelete, setRowToDelete] = useState<FinancialGridRow | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);

  const internalIdCounterRef = useRef<number>(0);

  // Add hasChanges state
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const handleDeleteClick = useCallback((row: FinancialGridRow) => {
    setRowToDelete(row);
    setDeleteConfirmOpen(true);
  }, []); // No dependencies needed

  const handleDeleteConfirm = useCallback(() => {
    if (rowToDelete) {
      // --- Use functional updates ---
      setDeletedRowIds(prevIds => {
        const nextIds = new Set(prevIds);
        nextIds.add(rowToDelete.id);
        return nextIds;
      });

      setGridRows(prevRows => prevRows.filter(row => row.id !== rowToDelete.id));

      setEditedRows(prevEdits => {
        const nextEdits = { ...prevEdits };
        delete nextEdits[rowToDelete.id];
        return nextEdits;
      });

      // Set hasChanges to true when a row is deleted
      setHasChanges(true);
    }
    setDeleteConfirmOpen(false);
    setRowToDelete(null);
  // Dependencies: Only include external state/props/functions READ inside. Setters are stable.
  }, [rowToDelete, setHasChanges]); // setDeletedRowIds, setGridRows, setEditedRows are stable

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
    setRowToDelete(null);
  }, []); // No dependencies needed

  const processRowUpdate = useCallback(
    async (
      newRow: EnhancedFinancialGridRow,
      oldRow: EnhancedFinancialGridRow,
      params: { rowId: string }
    ): Promise<EnhancedFinancialGridRow> => {
      console.log('Process row update:', { newRow, oldRow });

      // If nothing changed, return the old row to avoid unnecessary updates
      if (JSON.stringify(newRow) === JSON.stringify(oldRow)) {
        return oldRow;
      }

      // Ensure all required EnhancedFinancialGridRow fields are present
      const updatedRow: EnhancedFinancialGridRow = {
        ...newRow,
        categoryName: String(newRow.categoryName ?? oldRow.categoryName ?? 'Default Category'),
        // Add more required fields here if needed
      };

      // Create a list to store edit records
      const editRecords: EditRecord[] = [];

      // --- Ensure category changes are always tracked as edits ---
      if (newRow.categoryId !== oldRow.categoryId) {
        editRecords.push({
          field: 'categoryId',
          oldValue: oldRow.categoryId,
          newValue: newRow.categoryId
        });
      }
      // Also track categoryName changes if present
      if (newRow.categoryName !== oldRow.categoryName) {
        editRecords.push({
          field: 'categoryName',
          oldValue: oldRow.categoryName,
          newValue: newRow.categoryName
        });
      }

      // Handle item name changes
      if (newRow.itemName !== oldRow.itemName) {
        editRecords.push({
          field: 'itemName',
          oldValue: oldRow.itemName,
          newValue: newRow.itemName
        });
      }

      // Handle year value changes
      const yearFields = Object.keys(newRow).filter(key => !isNaN(Number(key)));
      yearFields.forEach(year => {
        const newValue = newRow[year];
        const oldValue = oldRow[year];

        // Only add to edit records if the value has changed
        if (newValue !== oldValue) {
          // Ensure values are properly typed
          const typedNewValue = typeof newValue === 'number' ? newValue :
                              typeof newValue === 'string' ? newValue :
                              newValue === null ? '' :
                              String(newValue);

          const typedOldValue = typeof oldValue === 'number' ? oldValue :
                              typeof oldValue === 'string' ? oldValue :
                              oldValue === null ? '' :
                              String(oldValue);

          editRecords.push({
            field: year,
            oldValue: typedOldValue,
            newValue: typedNewValue
          });
        }
      });

      console.log('Edit records:', editRecords);

      // Add the edit records to our edited rows map
      if (editRecords.length > 0) {
        setEditedRows(prev => ({
          ...prev,
          [updatedRow.id]: editRecords
        }));

        // Update the grid rows optimistically
        setGridRows(prevRows =>
          prevRows.map(row => (row.id === oldRow.id ? updatedRow : row))
        );

        // Set hasChanges to true when edits are made
        setHasChanges(true);
      }

      return updatedRow;
    },
    [setEditedRows, setGridRows, setHasChanges]
  );

  const onProcessRowUpdateError = useCallback((error: Error) => {
    console.error('DataGrid Row Update Error:', error);
    // Consider showing a user-facing error message
    // setError('Failed to update row. Please try again.'); // Example using a passed setError
  }, []); // Include setError if used

  const handleSaveAllChanges = useCallback(async (
    selectedHotelId: string,
    financialData: FinancialDataAPI | null,
    selectedDocType: string,
    setSuccess: (message: string | null) => void,
    setError: (message: string | null) => void,
    fetchFinancialData: (hotelId: string) => Promise<void>
  ): Promise<void> => {
    if (!selectedHotelId || !selectedDocType) {
      setError("Cannot save: Missing hotel selection or document type.");
      return;
    }

    try {
      setSaving(true);

      // Prepare data for saving
      const currentGridRows = [...gridRows];
      const currentDeletedRowIds = new Set(deletedRowIds);
      const currentEditedRows = { ...editedRows };

      // Skip if no data to save
      if (
        (currentEditedRows && Object.keys(currentEditedRows).length === 0) &&
        currentDeletedRowIds.size === 0
      ) {
        setSuccess("No changes to save!");
        setSaving(false);
        return;
      }

      // Format for the API
      const batchData: Record<number, Array<{
        item_name: string;
        values: Record<string, number | null>;
        document_type: string;
        file_id: string;
        category_id?: number; // Add category_id field
        original_category_id?: number; // Original category ID for locating existing record
        original_item_name?: string; // Original item name for locating existing record
      }>> = {};
      const processedDeletedItems: { categoryId: number; itemName: string; documentType: string }[] = [];
      let baseFileId: string | undefined = undefined;

      // Find baseFileId from existing non-deleted rows or original data
      const findFileId = (catId: number, itemName: string): string | undefined => {
        // Check current grid rows first (might be edited/new but have ID)
        const gridRow = currentGridRows.find(r =>
          !currentDeletedRowIds.has(r.id) &&
          r.documentType === selectedDocType &&
          r.categoryId === catId &&
          r.itemName === itemName &&
          r.fileId
        );
        if (gridRow?.fileId) return gridRow.fileId;

        // Check original financialData if available
        return financialData?.data[selectedDocType]?.[catId]?.find(item => item.item_name === itemName)?.file_id;
      };

      // Process edited rows
      currentGridRows.forEach((row) => {
        // Skip rows not matching selected type or marked for deletion
        if (row.documentType !== selectedDocType || currentDeletedRowIds.has(row.id)) {
          return;
        }

        const edits = currentEditedRows[row.id] || [];
        const isEdited = edits.length > 0;

        // Only process if edited
        if (isEdited) {
          // Get the final category ID from edits or current row
          const categoryEdit = edits.find(e => e.field === 'categoryId');
          const finalCategoryId = categoryEdit ?
            (typeof categoryEdit.newValue === 'number' ? categoryEdit.newValue :
             typeof categoryEdit.newValue === 'string' ? Number(categoryEdit.newValue) :
             (row.categoryId || 0)) :
            (row.categoryId || 0);

          // Get the final item name from edits or current row
          const itemNameEdit = edits.find(e => e.field === 'itemName');
          const finalItemName = itemNameEdit ? String(itemNameEdit.newValue) : row.itemName;

          // Try find fileId from original or current state
          if (!baseFileId) {
            baseFileId = row.fileId || findFileId(row.categoryId, row.itemName);
          }

          // Process year fields
          const valueObj: Record<string, number> = {};
          const yearFields = Object.keys(row).filter(key => !isNaN(Number(key)));

          yearFields.forEach((year) => {
            const yearEdit = edits.find(e => e.field === year);
            const valueInput = yearEdit ? yearEdit.newValue : row[year];

            // Convert to number, defaulting to 0 if null/undefined/invalid
            const numValue = valueInput === null || valueInput === undefined ? 0 :
                           typeof valueInput === 'number' ? valueInput :
                           Number(valueInput);

            valueObj[year] = isNaN(numValue) ? 0 : numValue;
          });

          if (!batchData[finalCategoryId]) {
            batchData[finalCategoryId] = [];
          }

          batchData[finalCategoryId].push({
            item_name: finalItemName,
            values: valueObj,
            document_type: selectedDocType,
            file_id: baseFileId || row.fileId || findFileId(row.categoryId, row.itemName) || crypto.randomUUID(),
            category_id: finalCategoryId,
            // Add original identifying information to help backend locate existing record
            original_category_id: row.originalCategoryId || row.categoryId,
            original_item_name: row.originalItemName || row.itemName
          });
        }
      });

      // Process deleted items
      currentDeletedRowIds.forEach((rowId) => {
        const row = currentGridRows.find(r => r.id === rowId);
        if (row && row.documentType === selectedDocType) {
          processedDeletedItems.push({
            categoryId: row.categoryId,
            itemName: row.itemName,
            documentType: selectedDocType
          });
        }
      });

      // Skip save if no data to save
      const requiresSave = Object.keys(batchData).length > 0 || processedDeletedItems.length > 0;
      if (!requiresSave) {
        setSuccess("No changes requiring save operation detected for the selected document type.");
        setSaving(false);
        return;
      }

      console.log("Saving Payload:", {
        data: batchData,
        deletedItems: processedDeletedItems,
        fileId: baseFileId,
        documentType: selectedDocType,
      });

      const response = await api.post(`/ocr/hotel/${selectedHotelId}/save-financials`, {
        data: batchData,
        deletedItems: processedDeletedItems,
        fileId: baseFileId,
        documentType: selectedDocType,
      });

      if (response.status >= 200 && response.status < 300) {
        setSuccess('Financial data saved successfully!');
        setEditedRows({}); // Clear edits on success
        setDeletedRowIds(new Set()); // Clear deletions on success
        setHasChanges(false); // Reset hasChanges after successful save

        // Auto-sync financial data if we saved P&L data to ensure valuation analysis sees updates
        if (selectedDocType === 'Profit & Loss Statement') {
          try {
            console.log('🔄 Auto-syncing P&L data after save...');
            const syncResponse = await api.post(`/hotels/${selectedHotelId}/sync-financials`);
            console.log('✅ P&L data auto-sync completed:', syncResponse.data);
            setSuccess('Financial data saved and synchronized successfully! Valuation analysis will reflect these changes.');
          } catch (syncError: any) {
            console.warn('⚠️ Auto-sync failed (non-critical):', syncError.message);
            setSuccess('Financial data saved successfully! (Auto-sync warning: Valuation may need manual refresh)');
          }
        }

        // Add a small delay before fetching to ensure backend consistency
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log('Refreshing financial data after successful save...');
        await fetchFinancialData(selectedHotelId); // Re-fetch
      } else {
        const errorData = response.data;
        const message = errorData?.details || errorData?.message || `Failed to save changes (Status: ${response.status}).`;
        throw new Error(message);
      }
    } catch (err: unknown) {
      console.error('Error saving data:', err);
      const apiErr = err as ApiError;
      const responseError = apiErr.response?.data?.details || apiErr.response?.data?.message;
      setError(responseError || (err instanceof Error ? err.message : 'An unexpected error occurred while saving.'));
    } finally {
      setSaving(false);
    }
  }, [gridRows, editedRows, deletedRowIds, setHasChanges]);

// Add new item
const handleAddNewItem = useCallback(async (
  selectedHotelId: string,
  financialData: FinancialDataAPI | null, // Parameter, not dependency
  selectedDocType: string, // Parameter, not dependency
  setSuccess: (message: string | null) => void,
  setError: (message: string | null) => void,
  fetchFinancialData: (hotelId: string) => Promise<void>,
  fetchADRMetricsData?: (hotelId: string) => Promise<void>,
  fetchOccupancyMetricsData?: (hotelId: string) => Promise<void>,
  fetchRevPARMetricsData?: (hotelId: string) => Promise<void>
) => {
    setError(null);
    setSuccess(null);

    // Read latest state from refs or directly inside the handler
    const currentGridRows = gridRows; // Use state value directly

    const getFileId = (): string => {
        // Find fileId from current grid rows matching the doc type
        const existingRow = currentGridRows.find(row => row.documentType === selectedDocType && row.fileId);
        if (existingRow?.fileId) return existingRow.fileId;

        // Try finding from original data (if adding to existing category)
        if (financialData && addFormCategory !== '') {
             const categoryItems = financialData.data[selectedDocType]?.[addFormCategory as number];
             const itemWithFileId = categoryItems?.find(item => item.file_id);
             if (itemWithFileId?.file_id) return itemWithFileId.file_id;
        }
        // Generate new if none found
        return crypto.randomUUID();
    };

    const performAdd = async (endpoint: string, payload: Record<string, unknown>, successMessage: string, refreshFunc?: (hotelId: string) => Promise<void>) => {
        setSaving(true);
        try {
            const response = await api.post(endpoint, payload);
            if (response.status === 200 || response.status === 201) {
                setSuccess(successMessage);
                setAddFormItemName('');
                setAddFormYear('');
                setAddFormAmount('');
                // Reset category only for non-metric types maybe?
                if (!['ADR Metrics', 'Occupancy Metrics', 'RevPAR Metrics'].includes(selectedDocType) &&
                    !addFormItemName.toLowerCase().includes('revpar')) {
                   setAddFormCategory('');
                }
                // Refresh relevant data
                const refreshTargetFunc = refreshFunc || fetchFinancialData; // Default refresh
                await refreshTargetFunc(selectedHotelId);

                // Update grid rows immediately with the new item
                if (response.data && !['ADR Metrics', 'Occupancy Metrics', 'RevPAR Metrics'].includes(selectedDocType)) {
                    const amountValue = parseFloat(addFormAmount);
                    const newRow: FinancialGridRow = {
                        id: `${selectedDocType}-${addFormCategory}-${addFormItemName.trim()}`,
                        internalId: internalIdCounterRef.current++,
                        originalCategoryId: addFormCategory as number,
                        originalItemName: addFormItemName.trim(),
                        categoryId: addFormCategory as number,
                        itemName: addFormItemName.trim(),
                        documentType: selectedDocType,
                        fileId: payload.file_id as string,
                        [addFormYear]: amountValue // Use the parsed amount value
                    };
                    console.log('Adding new row with amount:', amountValue, 'for year:', addFormYear);
                    setGridRows(prevRows => [...prevRows, newRow]);
                }
            } else {
                throw new Error(response.data?.details || response.data?.message || `Failed to add record (Status: ${response.status})`);
            }
        } catch (error: unknown) {
            console.error(`Error adding new item (${selectedDocType}):`, error);
            const apiErr = error as ApiError;
            const errorMsg = error instanceof Error
                ? error.message
                : apiErr.response?.data?.details || 'Failed to add new record';
            setError(errorMsg || `Failed to add new ${selectedDocType} record.`);
        } finally {
            setSaving(false);
        }
    };

    // --- Specific Logic per Document Type ---
    if (['ADR Metrics', 'Occupancy Metrics', 'RevPAR Metrics'].includes(selectedDocType)) {
        if (!addFormItemName.trim() || !addFormYear || addFormYear === 'custom' || !addFormAmount.trim() || !selectedHotelId) {
            setError(`Please fill in all fields for the new ${selectedDocType} record.`);
            return;
        }
        const yearValue = parseInt(addFormYear);
        if (isNaN(yearValue) || yearValue < 1900 || yearValue > 2100) {
            setError('Please enter a valid year (1900-2100).');
            return;
        }
        const amountValue = parseFloat(addFormAmount);
        if (isNaN(amountValue)) {
            setError('Amount/Value must be a valid number.');
            return;
        }

        let endpoint = '';
        let refreshFunc: ((hotelId: string) => Promise<void>) | undefined;

        switch (selectedDocType) {
            case 'ADR Metrics':
                endpoint = `/ocr/hotel/${selectedHotelId}/adr-metrics`;
                refreshFunc = fetchADRMetricsData;
                break;
            case 'Occupancy Metrics':
                 if (amountValue < 0 || amountValue > 100) {
                    setError('Occupancy must be a percentage between 0 and 100.');
                    return;
                 }
                endpoint = `/ocr/hotel/${selectedHotelId}/occupancy-metrics`;
                refreshFunc = fetchOccupancyMetricsData;
                break;
            case 'RevPAR Metrics':
                endpoint = `/ocr/hotel/${selectedHotelId}/revpar-metrics`;
                refreshFunc = fetchRevPARMetricsData;
                break;
        }

        const payload = {
            month: addFormItemName.trim(),
            year: yearValue,
            value: amountValue,
            fileId: getFileId()
        };
         // Ensure refreshFunc is defined before calling performAdd
        if (endpoint && refreshFunc) {
           await performAdd(endpoint, payload, `${selectedDocType} value for ${addFormItemName} (${yearValue}) added successfully!`, refreshFunc);
        } else if (endpoint) {
           // Fallback if optional metric refresh function wasn't provided (e.g., RevPAR)
           await performAdd(endpoint, payload, `${selectedDocType} value for ${addFormItemName} (${yearValue}) added successfully!`, fetchFinancialData);
        } else {
             setError("Configuration error: Endpoint or refresh function missing for metric type.");
        }

    } else { // Regular financial items (e.g., P&L)
        if (addFormCategory === '' || !addFormItemName.trim() || !selectedHotelId || !addFormYear.trim() || !addFormAmount.trim() || !selectedDocType) {
            setError('Please fill in all fields (Category, Item Name, Year, Amount) for the new item.');
            return;
        }
        const amountValue = parseFloat(addFormAmount);
        if (isNaN(amountValue)) {
            setError('Amount must be a valid number.');
            return;
        }
        const yearValue = parseInt(addFormYear);
        if (isNaN(yearValue) || yearValue < 1900 || yearValue > 2100) {
            setError('Please enter a valid year (1900-2100).');
            return;
        }

        // Create values object: only include the specified year
        const valuesObject = { [addFormYear]: amountValue };

        const newItemPayload = {
            category_id: addFormCategory,
            item_name: addFormItemName.trim(),
            values: valuesObject, // Send the values object directly, not stringified
            file_id: getFileId(),
            document_type: selectedDocType,
        };

        console.log('Sending payload with values:', valuesObject);

        await performAdd(
            `/ocr/hotel/${selectedHotelId}/add-item`,
            newItemPayload,
            'Item added successfully!',
            fetchFinancialData // Use the general fetch for standard items
        );
    }
  }, [
      // Dependencies: Form state, selected types, gridRows (for fileId), potentially financialData
      addFormCategory, addFormItemName, addFormYear, addFormAmount, gridRows,

      // setSuccess, setError, fetchFinancialData, fetchADRMetricsData, fetchOccupancyMetricsData, fetchRevPARMetricsData
      // State setters called directly are stable: setSaving, setAddForm...
  ]);


  // Update grid rows state based on financial data
   const updateGridRowsFromFinancialData = useCallback(
        (financialDataInput: FinancialDataAPI | null) => {
             console.log('Updating grid rows from financial data:', financialDataInput);

             // Log detailed info about the financial data structure
             if (financialDataInput) {
                 console.log('Financial data structure:',{
                     hasData: !!financialDataInput.data,
                     dataKeys: financialDataInput.data ? Object.keys(financialDataInput.data) : [],
                     years: financialDataInput.years,
                     categories: financialDataInput.categories
                 });

                 // Check for specific document types
                 Object.keys(financialDataInput.data || {}).forEach(docType => {
                     console.log(`Document type: ${docType} has data:`, !!financialDataInput.data[docType]);
                 });
             }

             // Use the utility function, passing the ref
             const newRows = transformFinancialDataToGridRows(financialDataInput, internalIdCounterRef);
             console.log('Transformed rows count:', newRows.length);
             if (newRows.length > 0) {
                 console.log('Sample row:', newRows[0]);
             }

             // Always set the grid rows, even if empty
             setGridRows(newRows); // Use the standard setter
        },
        [setGridRows] // setGridRows is stable
    );

  // Initialize error state if not already set
  const [error, setError] = useState<string | null>(null);

  // Define missing methods
  const resetEditedRows = useCallback(() => {
    setEditedRows({});
  }, []);

  const saveChanges = useCallback(async () => {
    // Implement save changes logic here
    setSaving(true);
    try {
      // Add your save logic
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  }, []);

  const handleRowUpdate = useCallback(async (updatedRow: FinancialGridRow) => {
    // Implement row update logic
    return updatedRow;
  }, []);

  const handleRowUpdateError = useCallback((error: Error) => {
    setError(error.message);
  }, []);

  // Wrapper for 2-arg signature
  const processRowUpdate2Arg = async (
    newRow: FinancialGridRow,
    oldRow: FinancialGridRow
  ): Promise<FinancialGridRow> => {
    const result = await processRowUpdate(
      newRow as EnhancedFinancialGridRow,
      oldRow as EnhancedFinancialGridRow,
      { rowId: newRow.id || '' }
    );
    return result as FinancialGridRow;
  };

  return {
    gridRows,
    editedRows,
    error,
    loading: false, // Default to false
    deletedRowIds,
    addFormCategory,
    addFormItemName,
    addFormYear,
    addFormAmount,
    saving,
    rowToDelete,
    deleteConfirmOpen,
    // Return standard setters
    setGridRows,
    setEditedRows,
    setDeletedRowIds,
    setSaving,
    setRowToDelete,
    setDeleteConfirmOpen,
    // Form setters
    setAddFormCategory,
    setAddFormItemName,
    setAddFormYear,
    setAddFormAmount,
    // Handlers
    handleRowUpdate,
    handleRowUpdateError,
    resetEditedRows,
    saveChanges,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    processRowUpdate,
    processRowUpdate2Arg,
    onProcessRowUpdateError,
    handleSaveAllChanges,
    handleAddNewItem,
    updateGridRowsFromFinancialData,
    hasChanges,
    setHasChanges
  };

}

export default useEditableData;